import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTokens, createToken, deleteToken, startOAuth } from '../api/tokens';
import { getApps } from '../api/apps';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';

export function TokensPage() {
  const qc = useQueryClient();
  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState({ customerId: '', tool: '', accessToken: '', refreshToken: '', tokenUrl: '', metadata: '' });
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: tokens = [] } = useQuery({ queryKey: ['tokens'], queryFn: getTokens });
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });

  const createMutation = useMutation({
    mutationFn: () => createToken({
      customerId: form.customerId, tool: form.tool, accessToken: form.accessToken,
      refreshToken: form.refreshToken || undefined, tokenUrl: form.tokenUrl || undefined,
      metadata: form.metadata || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tokens'] });
      setShowManual(false);
      setForm({ customerId: '', tool: '', accessToken: '', refreshToken: '', tokenUrl: '', metadata: '' });
      setCreateError(null);
    },
    onError: () => setCreateError('Failed to save token.'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  });

  const [oauthCustomerId, setOauthCustomerId] = useState('');
  const [selectedApp, setSelectedApp] = useState('');

  function handleAuthorize() {
    if (!selectedApp || !oauthCustomerId) return;
    startOAuth(selectedApp, oauthCustomerId).then(r => {
      window.location.href = r.authUrl;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Tokens" description="OAuth and API tokens per customer, read by Token Lookup steps." />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Start OAuth flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Select value={selectedApp || 'none'} onValueChange={v => setSelectedApp(v === 'none' ? '' : v)}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select app…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select app…</SelectItem>
                {apps.map(a => (
                  <SelectItem key={a.id} value={a.slug}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Customer ID"
              value={oauthCustomerId}
              onChange={e => setOauthCustomerId(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleAuthorize} disabled={!selectedApp || !oauthCustomerId}>
              Authorize
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-sm font-semibold">Manual token</h2>
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowManual(v => !v)}>
            {showManual ? 'Hide' : 'Add manually'}
          </Button>
        </div>
        {showManual && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              {(['customerId', 'tool', 'accessToken', 'refreshToken', 'tokenUrl'] as const).map(k => (
                <div key={k} className="space-y-1">
                  <Label htmlFor={`tok-${k}`} className="text-xs text-muted-foreground capitalize">{k}</Label>
                  <Input
                    id={`tok-${k}`}
                    value={form[k]}
                    onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="space-y-1">
                <Label htmlFor="tok-metadata" className="text-xs text-muted-foreground">Metadata</Label>
                <Textarea
                  id="tok-metadata"
                  rows={3}
                  className="font-mono text-xs"
                  value={form.metadata}
                  onChange={e => setForm(f => ({ ...f, metadata: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Free-form text, available in the flow as <code>{'{{nodeId.metadata}}'}</code>.
                </p>
              </div>
              {createError && (
                <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                  {createError}
                </p>
              )}
              <Button
                onClick={() => { setCreateError(null); createMutation.mutate(); }}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Tool</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tokens.map(t => (
            <TableRow key={t.id}>
              <TableCell className="font-medium">{t.customerId}</TableCell>
              <TableCell>{t.tool}</TableCell>
              <TableCell className="text-muted-foreground">
                {t.expiresAt ? new Date(t.expiresAt).toLocaleString() : '—'}
              </TableCell>
              <TableCell className="text-muted-foreground">{new Date(t.updatedAt).toLocaleDateString()}</TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => { if (window.confirm('Delete token?')) deleteMutation.mutate(t.id); }}
                  >
                    Delete
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {tokens.length === 0 && (
            <TableRow><TableCell colSpan={5} className="p-6 text-center text-muted-foreground">No tokens</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
